import React, { useState } from "react";
import { FaCode, FaCartShopping, FaGear, FaPen, FaRegFileLines, FaShieldHalved } from "react-icons/fa6";
import { ArrowCircle, Container, H3_SIZE, SectionHeading, tones } from "./ui";

const items = [
  {
    tone: "blue",
    icon: FaCode,
    title: "Website Development",
    img: "/img/website-development.svg",
    text: "Fast, responsive, and visually engaging websites that turn visitors into customers.",
    href: "/service/website-design-development-solutions",
  },
  {
    tone: "violet",
    icon: FaCartShopping,
    title: "E-commerce Development",
    img: "/img/ecommerce-development.svg",
    text: "Powerful online stores with seamless shopping experiences, secure checkout, and easy product management.",
    href: "/service/ecommerce-website-design-development-solutions",
  },
  {
    tone: "green",
    icon: FaGear,
    title: "Custom Web Applications",
    img: "/img/custom-web-applications.svg",
    text: "Tailor-made web applications designed around your unique business processes and goals.",
    href: "/service/custom-website-design-development-solutions",
  },
  {
    tone: "orange",
    icon: FaPen,
    title: "UI/UX Design",
    img: "/img/ui-ux-design.svg",
    text: "Intuitive interfaces and user-focused experiences that make every interaction simple and meaningful.",
    href: "/contact-us",
  },
  {
    tone: "pink",
    icon: FaRegFileLines,
    title: "CMS Development",
    img: "/img/cms-development.svg",
    text: "Easy-to-manage websites that let your team update content without depending on developers.",
    href: "/service/cms-website-design-development-solutions",
  },
  {
    tone: "blue",
    icon: FaShieldHalved,
    title: "Maintenance & Support",
    img: "/img/maintenance-support.svg",
    text: "Keep your website secure, updated, optimized, and running smoothly as your business grows.",
    href: "/contact-us",
  },
];

// Section background image. Put the file at public/img/what-we-do-bg.jpg (or change the path).
// If the file is missing, the soft gradient underneath is shown instead.
const sectionBg = "/img/what-we-do-bg.jpg";

// Per-tone colour for the card number.
const cardTones = {
  blue: { num: "text-[#1E6BFF]" },
  violet: { num: "text-[#9333EA]" },
  green: { num: "text-[#16A34A]" },
  orange: { num: "text-[#F97316]" },
  pink: { num: "text-[#E11D74]" },
};

// Card illustration: shows the image from `img`; if it is missing, falls back to the icon.
const CardArt = ({ item }) => {
  const [failed, setFailed] = useState(false);
  const t = tones[item.tone];
  if (item.img && !failed) {
    return (
      <img
        src={item.img}
        alt=""
        aria-hidden="true"
        onError={() => setFailed(true)}
        className="h-full w-full object-contain transition duration-300 group-hover:-translate-y-1"
      />
    );
  }
  return (
    <span className="relative flex h-[96px] w-[120px] flex-col overflow-hidden rounded-2xl bg-white/90 shadow-card ring-1 ring-white transition duration-300 group-hover:-translate-y-1">
      
      <span className={`flex flex-1 items-center justify-center text-[30px] ${t.text}`}>
        <item.icon />
      </span>
    </span>
  );
};

const WhatWeDo = () => (
  <section
    className="relative overflow-hidden bg-[#F6F8FF] bg-cover bg-center bg-no-repeat py-[72px] lg:py-[66px]"
    style={{ backgroundImage: `url(${sectionBg})` }}
  >
    <Container className="relative">
      <SectionHeading
        badge="What we do"
        lined
        title="Everything You Need to Build"
        highlight="Your Digital Presence"
        subtitle="We combine creative thinking, modern technology, and business strategy to create digital solutions that make a difference."
      />
      <div className="m-swipe mt-[48px] grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => {
          const c = cardTones[it.tone];
          return (
            <a
              key={it.title}
              href={it.href}
              className="group relative flex min-h-[200px] flex-col overflow-hidden rounded-[22px] border-[1px] border-solid border-[#EEF1F8] bg-white p-[24px] shadow-[0_10px_34px_-16px_rgba(40,60,140,0.18)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_-18px_rgba(40,60,140,0.28)] sm:p-[26px]"
            >
              {/* Illustration (its soft blob is part of the image) */}
              <div className="pointer-events-none absolute right-[10px] top-1/2 h-[150px] w-[133px] -translate-y-[58%] sm:right-[14px] sm:h-[170px] sm:w-[150px]">
                <CardArt item={it} />
              </div>

              <div className="relative flex-1">
                <p className={`text-[20px] font-bold leading-none ${c.num}`}>{String(i + 1).padStart(2, "0")}.</p>
                <h3 className={`mt-[14px] font-semibold text-ink ${H3_SIZE}`}>{it.title}</h3>
                <p className="mt-[8px] max-w-[62%] text-[12px] leading-[1.5] text-slate-500">{it.text}</p>
              </div>
              <ArrowCircle tone={it.tone} className="relative mt-[18px]" />
            </a>
          );
        })}
      </div>
    </Container>
  </section>
);

export default WhatWeDo;
